import Axios from 'axios';
import * as GradesCollectionRequest from '@/models/gradesCollectionRequest';
import * as GradesCollectionResponse from '@/models/gradesCollectionResponse';
import { Person } from '@/models/person';
import { Tag } from '@/models/tag';
import { Subject } from '@/models/subject';
import { UserTypes } from '@/enums/userTypes';
import { GradeYears } from '@/enums/gradeYears';
import { GradesTypes } from '@/enums/gradesTypes';
import { GenderTypes, getGenderTypeName } from '@/enums/genderTypes';
import { SchoolTypes, getSchoolTypeItems } from '@/enums/schoolTypes';
import { SchoolTerms, getSchoolTermSemesterItems, getSchoolTermTestSemesterItems } from '@/enums/schoolTerms';
import { ReturnTypes } from '@/enums/returnTypes';

export interface GradesCollection extends GradesCollectionResponse.GradesCollectionDetailResponse {
  schoolTerm: SchoolTerms | null;
}

export interface SchoolRecord extends GradesCollectionResponse.GradesCollectionDetailResponse {
  items: GradesCollection[];
}

export interface OtherTest extends GradesCollectionResponse.GradesCollectionDetailResponse {
  items: GradesCollectionResponse.GradesCollectionDetailResponse[];
}

export interface TestTypeItem {
  id: string;
  value: string;
  lastFiscalYear: number;
  lastTestTimes: number;
  schoolType: SchoolTypes;
  subjectList: Subject[];
}

export interface SendMessage {
  gradesCollectionId: string;
  schoolTerm: SchoolTerms;
  title: string;
  body: string;
  returnType: ReturnTypes;
  studentList: Person[];
}

interface State {
  loading: boolean;
  countAll: number;
  gradesCollectionList: GradesCollection[];
}
const mapState: State = {
  loading: false,
  countAll: 0,
  gradesCollectionList: new Array<GradesCollection>(),
};

const mapGetters = {
  schoolRecordCollection: (state: State): SchoolRecord[] => {
    return state.gradesCollectionList.filter((item) => {
      return (item.gradesType === GradesTypes.Report
        || item.gradesType === GradesTypes.RoutineTest);
    }).sort((a, b) => {
      if (a.gradesType < b.gradesType) { return -1; }
      if (a.gradesType > b.gradesType) { return 1; }
      if (a.schoolType < b.schoolType) { return -1; }
      if (a.schoolType > b.schoolType) { return 1; }
      return 0;
    }).reduce((list, schoolRecord: any) => {
      const schoolRecordItem = schoolRecord as SchoolRecord;
      schoolRecordItem.items = new Array<GradesCollection>();
      if (schoolRecordItem.gradesType === GradesTypes.RoutineTest) {
        getSchoolTermTestSemesterItems(false).forEach((schoolTerm) => {
          const item = JSON.parse(JSON.stringify(schoolRecord)) as GradesCollection;
          item.schoolTerm = schoolTerm.id;
          schoolRecordItem.items.push(item);
        });
      } else {
        getSchoolTermSemesterItems(false).forEach((schoolTerm) => {
          const item = JSON.parse(JSON.stringify(schoolRecord)) as GradesCollection;
          item.schoolTerm = schoolTerm.id;
          schoolRecordItem.items.push(item);
        });
      }
      list.push(schoolRecordItem);
      return list;
    }, new Array<SchoolRecord>());
  },
  otherTestCollection: (state: State) => {
    const rtn = state.gradesCollectionList.sort((a, b) => {
      if (a.testDay < b.testDay) { return 1; }
      if (a.testDay > b.testDay) { return -1; }
      if (a.fiscalYear < b.fiscalYear) { return 1; }
      if (a.fiscalYear > b.fiscalYear) { return -1; }
      if (a.testTypeName < b.testTypeName) { return -1; }
      if (a.testTypeName > b.testTypeName) { return 1; }
      if (a.testTimes < b.testTimes) { return 1; }
      if (a.testTimes > b.testTimes) { return -1; }
      return 0;
    }).reduce((list, otherTest) => {
      if (otherTest.gradesType !== GradesTypes.OtherTest) { return list; }
      let findItem = list.find((test) => test.testType === otherTest.testType);
      if (!findItem) {
        findItem = JSON.parse(JSON.stringify(otherTest)) as OtherTest;
        findItem.items = new Array<GradesCollectionResponse.GradesCollectionDetailResponse>();
        list.push(findItem);
      }
      findItem.items.push(otherTest);
      return list;
    }, new Array<OtherTest>());
    rtn.forEach((otherTest) => {
      otherTest.items = otherTest.items.sort((a, b) => {
        if (a.fiscalYear < b.fiscalYear) { return 1; }
        if (a.fiscalYear > b.fiscalYear) { return -1; }
        if (a.testTimes < b.testTimes) { return 1; }
        if (a.testTimes > b.testTimes) { return -1; }
        if (a.testDay < b.testDay) { return 1; }
        if (a.testDay > b.testDay) { return -1; }
        return 0;
      });
    });
    return rtn;
  },
  testTypeList: (state: State) => {
    return state.gradesCollectionList.reduce((list, item) => {
      if (item.gradesType !== GradesTypes.OtherTest) { return list; }
      const checkItem = list.find((check) => check.value === item.testType);
      if (!checkItem) {
        list.push({
          id: item.testType,
          value: item.testTypeName,
          lastFiscalYear: item.fiscalYear,
          lastTestTimes: item.testTimes,
          schoolType: item.schoolType,
          subjectList: JSON.parse(JSON.stringify(item.subjectList)),
        });
      } else {
        if (checkItem.lastFiscalYear < item.fiscalYear ||
          (checkItem.lastFiscalYear === item.fiscalYear && checkItem.lastTestTimes < item.testTimes)
        ) {
          checkItem.lastFiscalYear = item.fiscalYear;
          checkItem.lastTestTimes = item.testTimes;
          checkItem.schoolType = item.schoolType;
          checkItem.subjectList = JSON.parse(JSON.stringify(item.subjectList));
        }
      }
      return list;
    }, new Array<TestTypeItem>()).sort((a, b) => {
      if (a.value < b.value) { return -1; }
      if (a.value > b.value) { return 1; }
      return 0;
    });
  }
};

const mapActions = {
  async fetchGradesCollectionList({ commit }: any) {
    commit('setLoading', true);
    const response = await Axios.post('/api/GradesCollection/ListView').catch((error) => {
      commit('setLoading', false);
      throw error;
    });
    response.data.gradesCollection.forEach((gradesCollection: any) => {
      if (typeof gradesCollection.testDay === 'string') {
        gradesCollection.testDay = new Date(gradesCollection.testDay + 'Z');
      }
      gradesCollection.sendMessages.forEach((item: any) => {
        if (typeof item.lastSendDay === 'string') {
          item.lastSendDay = new Date(item.lastSendDay + 'Z');
        }
      });
    });
    commit('fetchGradesCollectionList', response.data);
  },
  async createGradesCollection({ commit }: any, gradesCollection: GradesCollection) {
    const request: GradesCollectionRequest.GradesCollectionDetailRequest = {
      gradesCollectionId: undefined,
      testType: gradesCollection.testType,
      testTypeName: gradesCollection.testTypeName,
      fiscalYear: gradesCollection.fiscalYear,
      testTimes: gradesCollection.testTimes,
      testDay: gradesCollection.testDay,
      schoolType: gradesCollection.schoolType,
      subjectIdList: gradesCollection.subjectList.map((item) => {
        return item.id;
      }),
    };
    const response = await Axios.post('/api/GradesCollection/Create', request).catch((error) => {
      if (!!error.response.data && !!error.response.data.TestTypeName) {
        throw new Error(error.response.data.TestTypeName);
      } else {
        throw new Error('成績収集情報の登録に失敗しました。');
      }
    });
    return response.data;
  },
  async editGradesCollection({ commit }: any, gradesCollection: GradesCollection) {
    const request: GradesCollectionRequest.GradesCollectionDetailRequest = {
      gradesCollectionId: gradesCollection.gradesCollectionId,
      testType: gradesCollection.testType,
      testTypeName: gradesCollection.testTypeName,
      fiscalYear: gradesCollection.fiscalYear,
      testTimes: gradesCollection.testTimes,
      testDay: gradesCollection.testDay,
      schoolType: gradesCollection.schoolType,
      subjectIdList: gradesCollection.subjectList.map((item) => {
        return item.id;
      }),
    };
    const response = await Axios.post('/api/GradesCollection/Edit', request).catch((error) => {
      throw new Error('成績収集情報の変更に失敗しました。');
    });
    commit('changeGradesCollection', gradesCollection);
    return response.data;
  },
  async editOtherTestAttribute({ commit }: any, gradesCollection: GradesCollection) {
    const request: GradesCollectionRequest.GradesCollectionDetailRequest = {
      gradesCollectionId: undefined,
      testType: gradesCollection.testType,
      testTypeName: gradesCollection.testTypeName,
      schoolType: gradesCollection.schoolType,
      fiscalYear: undefined,
      testTimes: undefined,
      testDay: undefined,
      subjectIdList: undefined,
    };
    const response = await Axios.post('/api/GradesCollection/EditAttribute', request).catch((error) => {
      throw new Error('成績収集情報の登録に失敗しました。');
    });
    commit('changeGradesCollection', gradesCollection);
    return response.data;
  },
  async createOtherTestTimes({ commit }: any, gradesCollection: GradesCollection) {
    const request: GradesCollectionRequest.GradesCollectionDetailRequest = {
      gradesCollectionId: undefined,
      testType: gradesCollection.testType,
      testTypeName: undefined,
      schoolType: undefined,
      fiscalYear: gradesCollection.fiscalYear,
      testTimes: gradesCollection.testTimes,
      testDay: gradesCollection.testDay,
      subjectIdList: gradesCollection.subjectList.map((item) => {
        return item.id;
      }),
    };
    const response = await Axios.post('/api/GradesCollection/CreateTestTimes', request).catch((error) => {
      if (!!error.response.data && !!error.response.data.TestType) {
        throw new Error(error.response.data.TestType);
      } else {
        throw new Error('年回の登録に失敗しました。');
      }
    });
    return response.data;
  },
  async editOtherTestTimes({ commit }: any, gradesCollection: GradesCollection) {
    const request: GradesCollectionRequest.GradesCollectionDetailRequest = {
      gradesCollectionId: gradesCollection.gradesCollectionId,
      testType: undefined,
      testTypeName: undefined,
      schoolType: undefined,
      fiscalYear: gradesCollection.fiscalYear,
      testTimes: gradesCollection.testTimes,
      testDay: gradesCollection.testDay,
      subjectIdList: gradesCollection.subjectList.map((item) => {
        return item.id;
      }),
    };
    const response = await Axios.post('/api/GradesCollection/EditTestTimes', request).catch((error) => {
      throw new Error('成績収集情報の変更に失敗しました。');
    });
    commit('changeGradesCollection', gradesCollection);
    return response.data;
  },
  async deleteGradesCollection({ commit }: any, gradesCollectionId: string) {
    const request: GradesCollectionRequest.GradesCollectionDetailRequest = {
      gradesCollectionId,
      testType: undefined,
      testTypeName: undefined,
      fiscalYear: undefined,
      testTimes: undefined,
      testDay: undefined,
      schoolType: undefined,
      subjectIdList: undefined,
    };
    const response = await Axios.post('/api/GradesCollection/Delete', request).catch((error) => {
      throw new Error('成績収集情報の削除に失敗しました。');
    });
    commit('removeGradesCollection', gradesCollectionId);
    return response.data;
  },
  async sendMessageGradesCollection({ commit }: any, sendMessage: SendMessage) {
    const request: GradesCollectionRequest.GradesCollectioMessageRequest = {
      gradesCollectionId: sendMessage.gradesCollectionId,
      schoolTerm: sendMessage.schoolTerm,
      title: sendMessage.title,
      body: sendMessage.body,
      returnType: sendMessage.returnType,
      studentIdList: sendMessage.studentList.map((student) => student.id),
      schoolType: undefined,
      testType: undefined,
      testTypeName: undefined,
      fiscalYear: undefined,
      testTimes: undefined,
      testDay: undefined,
      subjectIdList: undefined,
    };
    const response = await Axios.post('/api/GradesCollection/SendMessage', request).catch((error) => {
      throw error;
    });
    return response.data;
  },
  async getGradesCollection({ commit }: any, gradesCollectionId: string) {
    const request: GradesCollectionRequest.GradesCollectionDetailRequest = {
      gradesCollectionId,
      testType: undefined,
      testTypeName: undefined,
      fiscalYear: undefined,
      testTimes: undefined,
      testDay: undefined,
      schoolType: undefined,
      subjectIdList: undefined,
    };
    const response = await Axios.post('/api/GradesCollection/Detail', request).catch((error) => {
      throw error;
    });
    if (typeof response.data.testDay === 'string') {
      response.data.testDay = new Date(response.data.testDay + 'Z');
    }
    response.data.sendMessages.forEach((item: any) => {
      if (typeof item.lastSendDay === 'string') {
        item.lastSendDay = new Date(item.lastSendDay + 'Z');
      }
    });
    return response.data as GradesCollection;
  },
};

const mapMutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  fetchGradesCollectionList(state: State, data: GradesCollectionResponse.GradesCollectionListResponse) {
    state.countAll = data.countAll;
    state.gradesCollectionList = data.gradesCollection.map((item: any) => {
      item.selectedSemester = null;
      return item;
    });
    state.loading = false;
  },
  changeGradesCollection(state: State, gradesCollection: GradesCollection) {
    const findIndex = state.gradesCollectionList.findIndex((value) => {
      return (value.gradesCollectionId === gradesCollection.gradesCollectionId);
    });
    if (findIndex >= 0) {
      state.gradesCollectionList.splice(findIndex, 1, gradesCollection);
    }
  },
  removeGradesCollection(state: State, id: string) {
    const findIndex = state.gradesCollectionList.findIndex((value) => {
      return (value.gradesCollectionId === id);
    });
    if (findIndex >= 0) {
      state.gradesCollectionList.splice(findIndex, 1);
    }
  },
};

export default {
  namespaced: true,
  state: mapState,
  getters: mapGetters,
  actions: mapActions,
  mutations: mapMutations,
};
